import { useState } from "react";
import { Card } from "../common/Card";
import { Button } from "../common/Button";
import { Field } from "../common/Field";
import { ActionMenu } from "../common/ActionMenu";
import { DataTable } from "../common/DataTable";
import { LockedFeature } from "../common/LockedFeature";
import { Modal } from "../common/Modal";
import { downloadCsv } from "../../utils/csv";

/**
 * One standard report: pick a period, generate, then export (#316).
 *
 * WHY A COMPONENT AND NOT MORE OF ReportsPage. #316 warned this page
 * would go from 5 actions to 10-18 and that #327's action standard had
 * to exist first or it would become the worst offender on the board.
 * Each report is a surface that owns its own job -- one primary
 * (Generate), everything else behind a menu -- so adding the third and
 * fourth report costs one card each rather than four more buttons in a
 * row.
 *
 * THE EXPORTS ARE PER SECTION, and only appear once there is something
 * to export. A report has a summary, a breakdown by month and one by
 * vendor; there is no honest single CSV of all three, and what somebody
 * actually wants is "the monthly figures, in a spreadsheet". The menu
 * lists the sections that have rows -- an export that produces a file
 * containing only headers is worse than no export.
 *
 * QUARTER BUTTONS, not a quarter dropdown. The period is two dates
 * because a beekeeping year does not line up with a calendar one, but
 * the common asks are still "this quarter" and "this year" -- so they
 * are one click that FILLS THE DATES, leaving them visible and
 * editable. A dropdown would hide what was actually asked for.
 */

function startOfYear() {
  return `${new Date().getFullYear()}-01-01`;
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function quarterStart() {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) * 3;
  return `${now.getFullYear()}-${String(q + 1).padStart(2, "0")}-01`;
}

export function StandardReport({
  title,
  description,
  enabled,
  featureLabel,
  previewText,
  onGenerate,
  onPdf,
  onSchedule,
  scheduleLabel,
  sections,
  slug,
}) {
  const [start, setStart] = useState(startOfYear());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const [cadence, setCadence] = useState("Monthly");
  const [scheduleSaved, setScheduleSaved] = useState(null);
  const [scheduleError, setScheduleError] = useState(null);

  async function saveSchedule() {
    setScheduleError(null);
    try {
      const r = await onSchedule(cadence);
      // The DATE, not just "saved". "Monthly" is a rule; "first one on
      // 4 October" is the thing somebody can check against their inbox.
      setScheduleSaved(r?.next_run_date || null);
      setScheduling(false);
    } catch (err) {
      setScheduleError(err?.apiMessage || "Couldn't set that up. Try again.");
    }
  }

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      setData(await onGenerate(start, end));
    } catch (err) {
      // Never leave a stale report on screen under a new period -- the
      // numbers would look like an answer to the question just asked.
      setData(null);
      setError(err.apiMessage || "Could not generate that report.");
    } finally {
      setBusy(false);
    }
  }

  // Only sections that actually returned rows. A menu offering "By
  // vendor (CSV)" on a report with no vendors is a click that produces
  // an empty file.
  const exportable = data
    ? sections.filter((s) => Array.isArray(data[s.key]) && data[s.key].length > 0)
    : [];

  return (
    <Card title={title} className="section-spacing">
      <LockedFeature enabled={enabled} featureLabel={featureLabel} previewText={previewText}>
        {description && (
          <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>{description}</p>
        )}

        <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end" }}>
          <Field label="From">
            {(id) => (
              <input id={id} type="date" className="touch-target"
                value={start} onChange={(e) => setStart(e.target.value)} />
            )}
          </Field>
          <Field label="To">
            {(id) => (
              <input id={id} type="date" className="touch-target"
                value={end} onChange={(e) => setEnd(e.target.value)} />
            )}
          </Field>

          {/* Shortcuts that FILL the dates rather than replacing them, so
              what was asked for stays on screen. Ghost, because they are
              closer to navigation than to doing (rule 3). */}
          <Button size="sm" variant="ghost"
            onClick={() => { setStart(quarterStart()); setEnd(today()); }}>
            This quarter
          </Button>
          <Button size="sm" variant="ghost"
            onClick={() => { setStart(startOfYear()); setEnd(today()); }}>
            This year
          </Button>

          {/* Order is rule 8: secondary first, primary last. */}
          {data && (
            <ActionMenu
              label="Export"
              items={[
                onPdf && {
                  label: "Whole report (PDF)",
                  onSelect: () => onPdf(start, end),
                },
                ...exportable.map((s) => ({
                  label: `${s.label} (CSV)`,
                  onSelect: () => downloadCsv(data[s.key], `${slug}-${s.key}-${start}-to-${end}.csv`),
                })),
              ].filter(Boolean)}
            />
          )}
          {/* SCHEDULING LIVES ON THE REPORT (#317's open UI question,
              answered against #327). You are looking at the thing you
              want monthly -- that is the discoverable moment, and it is
              the only place the report kind is unambiguous.

              Settings gets the LIST of everything arriving in an inbox,
              because that is where somebody goes to stop things. Both,
              as the issue guessed: create where you are, manage the set
              where you would look to end it.

              Secondary, not primary: rule 1 says one primary per
              surface, and that is Generate. */}
          {onSchedule && (
            <Button variant="secondary" onClick={() => setScheduling(true)}>
              Schedule…
            </Button>
          )}
          <Button onClick={generate} disabled={busy || !start || !end}>
            {busy ? "Generating…" : "Generate"}
          </Button>
        </div>

        {error && <p role="alert" style={{ color: "var(--color-danger)" }}>{error}</p>}
        {scheduleError && (
          <p role="alert" style={{ color: "var(--color-danger)" }}>{scheduleError}</p>
        )}
        {scheduleSaved && (
          <p style={{ color: "var(--color-success)" }}>
            {scheduleLabel || "This report"} will arrive by email. First one on {scheduleSaved}.
          </p>
        )}

        {/* A MODAL, not a ConfirmDialog: it has a field, and
            UI_CONFIRMATIONS_AND_PICKERS §1 says if it has a field it is
            not a confirmation. */}
        <Modal
          open={scheduling}
          onClose={() => setScheduling(false)}
          title={`Send ${scheduleLabel || "this report"} automatically`}
          footer={<Button onClick={saveSchedule}>Schedule it</Button>}
        >
          <Field label="How often">
            {(id) => (
              <select id={id} className="touch-target" value={cadence}
                      onChange={(e) => setCadence(e.target.value)}>
                <option value="BiWeekly">Every two weeks</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            )}
          </Field>
          <p style={{ color: "var(--color-text-muted)" }}>
            It goes to you. Team members can be added in Settings, and only
            those allowed to see money will receive a money report.
          </p>
        </Modal>

        {data && (
          <div style={{ marginTop: "var(--space-4)" }}>
            {sections.map((section) => {
              const rows = data[section.key];
              if (!Array.isArray(rows)) return null;
              return (
                <div key={section.key} style={{ marginBottom: "var(--space-4)" }}>
                  <h3 style={{ fontSize: "var(--font-size-md)", marginBottom: "var(--space-2)" }}>
                    {section.label}
                  </h3>
                  <DataTable
                    rows={rows}
                    rowKey={section.rowKey}
                    columns={section.columns}
                    emptyTitle={`No ${section.label.toLowerCase()} in this period`}
                    /* Says the period had none, rather than implying the
                       report failed. */
                    emptyDescription="Try a wider date range."
                  />
                </div>
              );
            })}
          </div>
        )}
      </LockedFeature>
    </Card>
  );
}
